import { memo, useMemo, useState, useEffect, ReactElement } from "react";
import GeneratePdf from "../../components/GeneratePdf";
import GenerateExcel from "../../components/GenerateExcel/index";
import DataGrid, { FormatterProps } from "react-data-grid";
import {
  Expander,
  ExpanderItem,
  SearchField,
  Tabs,
  TabItem,
  Card,
  Badge as BadgeUI,
  BadgeVariations,
} from "@aws-amplify/ui-react";
import { useForm, SubmitHandler } from "react-hook-form";
import { groupBy as rowGrouper } from "lodash";
import {
  MdPostAdd,
  MdTimeline,
  MdFactory,
  MdInfo,
  MdBrokenImage,
} from "react-icons/md";
import { BiCommentDetail } from "react-icons/bi";
import { AiOutlineCheckCircle, AiOutlineEdit } from "react-icons/ai";
import { TbPackageOff, TbPackageImport } from "react-icons/tb";
import { HiMagnifyingGlass } from "react-icons/hi2";
import { GiChemicalDrop } from "react-icons/gi";

import { CgLoadbarDoc, CgPlayListCheck } from "react-icons/cg";
import {
  getOrder,
  getActiveOperations,
  getOperationLogsAll,
  getReasonCodes,
} from "../../apis";
import {
  getRemotePicklistById,
  getMovementsById,
  extraPicklistsByOrderId,
  returnPicklistsByOrderId,
  getReturnPicklist,
  getFGInspectionLogsByOrder,
  getOrderReceipts,
} from "../../states/PicklistAction";
import {
  DgtPdOrderOperation,
  DgtPdOrderOperationLog,
  DgtPdOrderPicklist,
  DgtPdOrderPicklistExtraReturn,
  DgtPdOrderPicklistExtraReturnItem,
  DgtPdOrderPicklistMovement,
  DgtPdOrderReasonCode,
  DgtPdProductionOrder,
} from "../../models";
import {
  formatNumberV2,
  formatDateISO,
  formatDate,
  ceil2Decimal,
  formatDateFromUnix,
} from "../../utilities/Format";

import Comment from "../../components/Comment";
import Badge from "../../components/Badge";
import SpinnerLoading from "../../components/SpinnerLoading";

import {
  BulkInspectionLogColumn,
  OperationLogsColumns,
  PicklistExtraColumn,
  PicklistItemReportColumn,
  PicklistReturnColumn,
} from "../../constants/Column";
import { ReasonDetail } from "../../pages/AddLabOffice";
import { OrderInspectionLog, OrderPicklistMovementReport, OrderReceipt } from "../../components/types";
import { OrderStatusDisplay, QCDowntimeCodes } from "../../constants/Enum";
import { PicklistStatus } from "../../constants/EnumType";
import FGInspectMenu from "../../components/FGInspectMenu";
import _ from "lodash";
import { CiReceipt } from "react-icons/ci";
import RetainRecord from "../Form/RetainRecord";

interface Inputs {
  productionOrder: string;
}

const Lable = ({
  title,
  value,
  value2,
  badge,
  badgeVariant
}: {
  title: string;
  value: string | number;
  value2?: string;
  badge?: string;
  badgeVariant?: BadgeVariations;
}) => (
  <div>
    <div className="flex items-center gap-1"><p className=" text-muted">{title}</p>{badge && <BadgeUI size="small" variation={badgeVariant}>{badge}</BadgeUI>}</div>
    <p className="text-lg font-bold">{value ? value : "-"}</p>
    {value2 ? <p className="italic text-muted">{value2}</p> : null}
  </div>
);

const LableBadge = ({
  title,
  value,
  showInfo,
}: {
  title: string;
  value: ReactElement;
  showInfo: ReactElement;
}) => (
  <div className="flex gap-2 ">
    <div>
      <p className=" text-muted">{title}</p>
      {value}
    </div>
    <div className="flex gap-1 pt-1">
      <MdInfo className="cursor-pointer text-muted peer" />
      <div className="p-3 space-y-2 transition-opacity bg-gray-100 rounded-md opacity-0 peer-hover:opacity-100">
        {showInfo}
      </div>
    </div>
  </div>
);

export default memo(function Report() {
  const [order, setOrder] = useState<DgtPdProductionOrder>();
  const [operations, setOperations] = useState<DgtPdOrderOperation[]>([]);
  const [operationLogs, setOperationLogs] = useState<DgtPdOrderOperationLog[]>(
    []
  );
  const [packageReasonCodes, setReasonCodes] = useState<DgtPdOrderReasonCode[]>(
    []
  );
  const [movements, setMovements] = useState<DgtPdOrderPicklistMovement[]>([]);
  const [extras, setExtras] = useState<DgtPdOrderPicklistExtraReturn[]>([]);
  const [returns, setReturns] = useState<DgtPdOrderPicklistExtraReturn[]>([]);
  const [picklist, setPicklist] = useState<DgtPdOrderPicklist>();
  const [comments, setComments] = useState<DgtPdOrderPicklist[]>([]);
  const [picklistItems, setPicklistItems] = useState<DgtPdOrderPicklist[]>([]);
  const [ fgInspectionLogs, setFgInspectionLogs ] = useState<OrderInspectionLog[]>()
  const [ orderReceipts, setOrderReceipts ] = useState<OrderReceipt[]>([])

  const [expandedGroupIds, setExpandedGroupIds] = useState<
    ReadonlySet<unknown>
  >(() => new Set<unknown>([""]));

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    const order = await getOrder(data.productionOrder);
    setOrder(order);

    const operations = await getActiveOperations(data.productionOrder);
    setOperations(operations);

    const operationLogs = await getOperationLogsAll(data.productionOrder);
    setOperationLogs(operationLogs);

    const reasonCodes = await getReasonCodes(data.productionOrder);
    setReasonCodes(reasonCodes);

    const movements = await getMovementsById(data.productionOrder);
    setMovements(movements);

    const { header, comments, items } = await getRemotePicklistById(
      data.productionOrder
    );
    setComments(comments);
    setPicklist(header);
    setPicklistItems(items);

    const exts = await extraPicklistsByOrderId(data.productionOrder);
    setExtras(exts);

    const rets = await returnPicklistsByOrderId(data.productionOrder);
    const returnItemPromises = await Promise.all(
      rets.map((ret) => getReturnPicklist(ret.id))
    );
    returnItemPromises.sort((a, b) =>
      a.approvedDateTime! > b.approvedDateTime! ? 1 : -1
    );
    setReturns(returnItemPromises);

    const fgInspectionLogs = await getFGInspectionLogsByOrder(data.productionOrder)
    setFgInspectionLogs(fgInspectionLogs)

    const orderReceipts = await getOrderReceipts(data.productionOrder)
    setOrderReceipts(orderReceipts)
  };

  const reset = () => {
    setOrder(undefined);
    setPicklist(undefined);
  };

  let columns = Array.from(OperationLogsColumns).map((col) => ({
    ...col,
    editable: false,
  }));
  columns.splice(0, 1);
  columns.splice(columns.length - 2, 2);

  useEffect(() => {
    if (movements.length > 0) {
      setExpandedGroupIds(
        new Set(
          movements
            .filter(
              (mv) =>
                movements.filter((mv1) => mv1.materialCode === mv.materialCode)
                  .length > 1
            )
            .map((itm) => itm.materialCode)
        )
      );
    }
  }, [movements]);

  const firstLog = operationLogs.length > 0 ? operationLogs[0] : undefined;
  const lastOp = operations[operations.length - 1];

  const latestDraftedOp = useMemo(() => {
    const copyOps = [...operations];
    copyOps.sort((a, b) =>
      (b.draftedDate || "").localeCompare(a.draftedDate || "")
    );
    return copyOps[0];
  }, [operations]);

  const actualQty = useMemo(() => {
    let sumYield = operationLogs.reduce(
      (prev, { yield: y }) => prev + (y || 0),
      0
    );
    if (operations.length > 1) {
      const opFromSapOnly =
        operations.filter((op) => op.createdBySystem === "APP").length === 0;
      if (opFromSapOnly) {
        sumYield = operationLogs
          .filter(
            (log) =>
              log.operationNo === operations[operations.length - 1].operationNo
          )
          .reduce((prev, { yield: y }) => prev + (y || 0), 0);
      }
    }
    return ceil2Decimal(sumYield);
  }, [operations, operationLogs]);

  const addOnAndSortedMvs = useMemo(() => {
    const cloneMvs = [...movements] as OrderPicklistMovementReport[];
    cloneMvs.sort((a, b) =>
      a.documentCreatedAt! > b.documentCreatedAt! ? 1 : -1
    );
    const totalItems = extractItems(extras);
    return cloneMvs
      .map((mv) => {
        const firstExtra = extras[0];
        const item = totalItems.find(
          (itm) =>
            itm.materialCode === mv.materialCode &&
            mv.documentCreatedAt! > firstExtra.requestedDateTime!
        );

        if (!item) return mv;

        return {
          ...mv,
          isExtra: true,
        };
      })
      .map((mv) => {
        const barcodeItem = picklistItems.find(
          (itm) => itm.materialCode === mv.materialCode
        )?.barcodePkg;
        if (barcodeItem) return { ...mv, barcode: barcodeItem };
        return mv;
      });
  }, [movements, extras, picklistItems]);

  useEffect(() => {
    if (isSubmitting) reset();
  }, [isSubmitting]);

  const wasteRejectPkgMvs = useMemo(() => {
    const groupByMatCode = rowGrouper(
      movements.filter((mv) => mv.wasteRecordedDateTime! > 0),
      "materialCode"
    );
    return Object.keys(groupByMatCode).map((code) => ({
      materialCode: code,
      rejectLots: groupByMatCode[code],
    }));
  }, [movements]);

  let returnCols = Array.from(PicklistReturnColumn)
    .slice(3, PicklistReturnColumn.length - 1)
    .map((col) => (col.key === "remark" ? { ...col, width: 200 } : col));
  returnCols.push({
    key: "items",
    name: "รายการคืน",
    headerCellClass: "text-center",
    cellClass: "multi-line-row-data-grid",
    formatter: (props: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
      return (
        <div className="w-full space-y-4">
          {props.row
            .extraReturnItems!.filter(
              (retItm) =>
                (retItm?.qty || retItm?.withShQty || retItm?.wasteQty!) > 0
            )
            .map((retItm) => {
              let returnType = "ปกติ";
              let returnBadgeStyle = "success";
              if (retItm?.withShQty! > 0) {
                returnType = "ติด SH/STK";
                returnBadgeStyle = "bg-green-300";
              }
              if (retItm?.wasteQty! > 0) {
                returnType = "เสีย Reject";
                returnBadgeStyle = "bg-red-400 text-white";
              }
              return (
                <div className="grid grid-cols-3 gap-1" key={retItm?.id}>
                  <p className="col-span-3 font-bold ">
                    {retItm?.materialCode} - {retItm?.materialDescription}
                  </p>
                  <div>Batch Lot</div>
                  <p className="col-span-2 ">{retItm?.batchLotNo}</p>
                  <div>Qty</div>
                  <p className="col-span-2 italic font-bold">
                    {formatNumberV2(
                      retItm?.qty || retItm?.withShQty || retItm?.wasteQty
                    )}{" "}
                    {retItm?.materialUnit}
                    <Badge variant={`${returnBadgeStyle} ml-2 text-xs`}>
                      {returnType}
                    </Badge>
                  </p>
                  <div>Location</div>
                  <p className="col-span-2 ">
                    {retItm?.returnStorageLocation
                      ? retItm?.returnStorageLocation
                      : retItm?.wasteQty! > 0
                      ? "LREJ"
                      : "Loc เดิม"}
                  </p>
                </div>
              );
            })}
        </div>
      );
    },
  });

  const bulkInspectLogs = useMemo(
    () =>
      operationLogs.filter((log) =>
        QCDowntimeCodes.includes(log.downtimeCode || "")
      ),
    [operationLogs]
  );
  const bulkInspectLogsCols = Array.from(BulkInspectionLogColumn).slice(
    4,
    BulkInspectionLogColumn.length - 2
  );

  const fgInspectionWorkCenterIds = useMemo(() => {
    const groupedLogs = _.groupBy(fgInspectionLogs, 'workCenterId');
    return _.map(groupedLogs, (logs, workCenterId) => workCenterId);
  }, [ fgInspectionLogs ]);
  
  const mvColumns = useMemo(() => {
    const baseCols = Array.from(PicklistItemReportColumn)
    baseCols.push({
      key: 'vendorLotNo', name: 'Vendor Batch', width: 120,
      groupFormatter({ childRows }) {
        return <div className='font-bold'>{childRows.length === 1 ? childRows[0].vendorLotNo : '' }</div>
      }
    })
    return baseCols
  }, [])

  return (
    <div className="p-3 mb-5">
      <div className="flex flex-col items-center gap-3 ">
        <h3 className="underline text-muted ">Production Order Report</h3>
        {/* Search box */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <SearchField
            label="search"
            placeholder="Search..."
            onClear={reset}
            {...register("productionOrder")}
          />
        </form>
      </div>
      {/* Body */}
      {order && picklist ? (
        <>
          <div className="flex justify-end gap-2">
            {/* <button className='flex items-center gap-1 btn-outline-danger '><TbPdf size={20} />Download PDF</button> */}
            <GeneratePdf
              className="btn-nomal"
              type={"DOWNLOAD"}
              data={{
                order: order,
                actualQty: actualQty,
                picklistStatus: picklist.status!,
                operations: operations,
                operationLogs: operationLogs,
                bulkInspectLogs: bulkInspectLogs,
                addOnAndSortedMvs: addOnAndSortedMvs,
                extras: extras,
                packageReasonCodes: packageReasonCodes,
                wasteRejectPkgMvs: wasteRejectPkgMvs,
                returns: returns,
                picklist: picklist,
                firstLog: firstLog,
                latestDraftedOp: latestDraftedOp,
                lastOp: lastOp,
                comments: comments,
                orderReceipts: orderReceipts,
                fgInspectionLogs: fgInspectionLogs,
              }}
            />
            {/* <button className='flex items-center gap-1 btn-outline-success '><RiFileExcel2Line size={20} />Download Excel</button> */}
            <GenerateExcel
              className="btn-nomal"
              data={{
                order: order,
                actualQty: actualQty,
                picklistStatus: picklist.status!,
                operations: operations,
                operationLogs: operationLogs,
                bulkInspectLogs: bulkInspectLogs,
                addOnAndSortedMvs: addOnAndSortedMvs,
                extras: extras,
                packageReasonCodes: packageReasonCodes,
                wasteRejectPkgMvs: wasteRejectPkgMvs,
                returns: returns,
                picklist: picklist,
                firstLog: firstLog,
                latestDraftedOp: latestDraftedOp,
                lastOp: lastOp,
                comments: comments,
                orderReceipts: orderReceipts,
                fgInspectionLogs: fgInspectionLogs,
              }}
            />
          </div>
          <Tabs justifyContent="flex-start">
            <TabItem
              title={
                <div className="flex items-center justify-center gap-1">
                  <CgLoadbarDoc />
                  General Information
                </div>
              }
            >
              <div className="grid w-full grid-cols-1 gap-3 p-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <Lable title="Order#" value={order.id} />
                <Lable title="Material Code" value={order.materialCode} />
                <Lable
                  title="Material Desc"
                  value={order.materialDescription}
                />
                <Lable title="Barcode EAN" value={order.barcodeFgCase!} />
                <div>
                  <p className=" text-muted">Product Profile</p>
                  <div className="flex flex-wrap gap-1">
                    {order.infoFwt && (
                      <Badge variant="product-info">{order.infoFwt}</Badge>
                    )}
                    {order.infoEffBulkDate && (
                      <Badge variant="product-info">{`Eff.Bulk : ${order.infoEffBulkDate}`}</Badge>
                    )}
                    {order.infoRefPkg && (
                      <Badge variant="product-info">{order.infoRefPkg}</Badge>
                    )}
                    {order.infoPallet && (
                      <Badge variant="product-info">{`Pallet : ${order.infoPallet}`}</Badge>
                    )}
                    {order.infoPacking && (
                      <Badge variant="product-info">{`Packing : ${order.infoPacking}`}</Badge>
                    )}
                  </div>
                </div>
                <Lable title="Division" value={order.division!} />
                <Lable
                  title="Qty / Actual"
                  value={`${formatNumberV2(
                    order.orderQty || 0
                  )} / ${formatNumberV2(actualQty)} ${order.materialUnit}`}
                />
                <Lable
                  title="Released Date"
                  value={formatDateISO(order.createdAt)}
                />
                <Lable title="Released By" value={order.releasedBy!} />
                <Lable
                  title="Actual Due Date"
                  value={formatDate(order.actualDueDate!)}
                />
                <Lable
                  title="Actual Batch Lots"
                  value={
                    order.actualBatchNumbers
                      ? (JSON.parse(order.actualBatchNumbers) as string[]).join(
                          ", "
                        )
                      : "-"
                  }
                />
                <Lable title="Trial Batch" value={order.trialBatch!} />
                <LableBadge
                  title="Order Status"
                  value={displayOrderStatus(order.statusFromApp!)}
                  showInfo={
                    <>
                      {Object.entries(OrderStatusDisplay)
                        .filter((ent) => ent[0] !== "ALL")
                        .map((ent) => ent[0])
                        .map((k) => (
                          <div key={k}>{displayOrderStatus(k)}</div>
                        ))}
                    </>
                  }
                />
                <LableBadge
                  title="Picklist Status"
                  value={displayPicklistStatus(picklist.status!)}
                  showInfo={
                    <>
                      {Object.entries(PicklistStatus)
                        .map((ent) => ent[0])
                        .map((k) => (
                          <div key={k}>{displayPicklistStatus(k)}</div>
                        ))}
                    </>
                  }
                />
              </div>
            </TabItem>
            <TabItem
              title={
                <div className="flex items-center justify-center gap-1">
                  <CgPlayListCheck />
                  Order Confirmation{" "}
                  <Badge variant="info">{`${operations.length}`}</Badge>
                </div>
              }
            >
              <div className="py-5">
                <Expander
                  type="multiple"
                  defaultValue={operationLogs.map((log) => log.operationNo)}
                >
                  {operations.map((operation) => {
                    const logs = operationLogs.filter(
                      (log) => log.operationNo === operation.operationNo
                    );
                    const totalYield = ceil2Decimal(
                      logs.reduce((prev, { yield: y }) => prev + (y || 0), 0)
                    );
                    return (
                      <ExpanderItem
                        key={operation.id}
                        title={`OP. ${operation.operationNo} - ${
                          operation.actualWorkCenterDescription
                            ? operation.actualWorkCenterDescription
                            : operation.workCenterDescription
                        } (${
                          operation.actualWorkCenterId
                            ? operation.actualWorkCenterId
                            : operation.workCenterId
                        })`}
                        value={operation.operationNo}
                      >
                        <div className="space-y-4 ">
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            <Lable
                              title="Actual Qty"
                              value={`${formatNumberV2(totalYield)} ${
                                order.materialUnit
                              }`}
                            />
                            <Lable
                              title="Drafted by"
                              value={operation.draftedBy!}
                            />
                            <Lable
                              title="Drafted time"
                              value={formatDateISO(operation.draftedDate)}
                            />
                            <Lable
                              title="Confirmed by"
                              value={operation.confirmedBy!}
                            />
                            <Lable
                              title="Confirmed time"
                              value={formatDateISO(operation.confirmedDate)}
                            />
                            <Lable
                              title="Confirmation No"
                              value={operation.confirmationId!}
                            />
                          </div>
                          <DataGrid
                            style={{ height: "100%", contain: "none" }}
                            columns={columns}
                            rows={logs}
                            defaultColumnOptions={{ resizable: true }}
                          />
                        </div>
                      </ExpanderItem>
                    );
                  })}
                </Expander>
              </div>
            </TabItem>
            <TabItem
              title={
                <div className="flex items-center justify-center gap-1">
                  <HiMagnifyingGlass />
                  Inspection
                </div>
              }
            >
              <div className="p-5 space-y-6">
                {order.id.startsWith("8") ? (
                  <>
                    <div className="flex items-end gap-1">
                      <GiChemicalDrop size={26} className="fill-red-400" />
                      <p className="text-lg font-bold underline">
                        ผลการทดสอบ Bulk
                      </p>
                    </div>
                    <DataGrid
                      className="mt-4"
                      style={{ height: "100%", contain: "none" }}
                      columns={bulkInspectLogsCols}
                      rows={bulkInspectLogs}
                      rowHeight={(arg) => {
                        if (arg.type === "GROUP") return 40;
                        else return 50;
                      }}
                    />
                  </>
                ) : (
                  <>
                    {fgInspectionWorkCenterIds.map((workCenterId) => {
                      const logs = fgInspectionLogs?.filter(l => l.workCenterId === workCenterId)
                        .sort((a, b) => a.inspectionType.localeCompare(b.inspectionType))
                        .map(f => ({...f, canEdit: true }))
                      const workCenterDescription = logs?.[0]?.workCenterDescription
                      return <div key={workCenterId} className='py-5'>
                        <p className='text-xl font-bold'>Work Center : <span className='font-light'>{workCenterId} - {workCenterDescription}</span></p>
                        <div className='grid grid-cols-2 gap-5 py-5 lg:grid-cols-4'>
                        {logs?.map(form => (
                          <FGInspectMenu
                            key={`${form.inspectionType}-${form.id}`}
                            formLog={form}
                            hideArrow={true}
                            isShowLink={true}
                            approved={true}
                          />
                        ))}
                        </div>
                      </div>
                    })}
                  </>
                )}
              </div>
            </TabItem>
            <TabItem
              title={
                <div className="flex items-center justify-center gap-1">
                  <MdFactory />
                  BOM Usage
                </div>
              }
            >
              <div className="p-5 space-y-6">
                <DataGrid
                  style={{ height: "100%", contain: "none" }}
                  columns={mvColumns}
                  rows={addOnAndSortedMvs}
                  rowKeyGetter={(row) => row.id}
                  groupBy={["materialCode"]}
                  rowGrouper={rowGrouper}
                  expandedGroupIds={expandedGroupIds}
                  onExpandedGroupIdsChange={setExpandedGroupIds}
                  defaultColumnOptions={{ resizable: true }}
                  rowHeight={(arg) => {
                    if (arg.type === "GROUP" && arg.row.childRows[0].barcode)
                      return 55;
                    return 35;
                  }}
                />
                {extras.length > 0 ? (
                  <div className="space-y-2 ">
                    <div className="flex items-end gap-1">
                      <MdPostAdd size={26} className="fill-yellow-500" />
                      <p className="text-lg font-bold underline">
                        รายการเบิกเพิ่ม
                      </p>
                    </div>
                    <DataGrid
                      className="mb-3 "
                      style={{ height: "100%", contain: "none" }}
                      columns={PicklistExtraColumn}
                      rows={extras}
                      defaultColumnOptions={{ resizable: true }}
                      rowHeight={(row) => {
                        if (row.type === "GROUP") return 35;
                        const items = row.row.items
                          ? JSON.parse(row.row.items)
                          : [];
                        return (items.length === 0 ? 1 : items.length) * 40;
                      }}
                    />
                  </div>
                ) : null}
                {packageReasonCodes.length > 0 ? (
                  <div className="space-y-2 ">
                    <div className="flex items-end gap-1">
                      <MdBrokenImage size={26} className="fill-red-500" />
                      <p className="text-lg font-bold underline ">
                        เหตุผลของเสีย (ใช้เสีย)
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {packageReasonCodes.map((mat) => {
                        const reasons = JSON.parse(
                          mat.reasonCodes || ""
                        ) as ReasonDetail[];
                        const totalScrap = ceil2Decimal(
                          reasons.reduce(
                            (prev, cur) => prev + Number(cur.quantity),
                            0
                          )
                        );
                        return totalScrap > 0 ? (
                          <Card key={mat.id} variation="elevated">
                            <div className="flex justify-between ">
                              <h6 className="font-bold ">
                                {mat.materialDescription}
                              </h6>
                              <Badge variant="info text-xs h-fit">
                                {mat.labOffice}
                              </Badge>
                            </div>
                            <p className="mb-3 ">{mat.materialCode}</p>
                            {reasons
                              .filter((reason) => Number(reason.quantity) > 0)
                              .map((reason) => (
                                <div
                                  key={reason.reasonCode}
                                  className="flex justify-between "
                                >
                                  <p>• {reason.reasonDescription}</p>
                                  <span>
                                    {formatNumberV2(reason.quantity)} {mat.unit}
                                  </span>
                                </div>
                              ))}
                            <div
                              className={` flex justify-end items-baseline mt-3 font-bold text-xl text-red-500 before:content-['รวม'] before:text-sm before:mr-2 before:text-black`}
                            >
                              {formatNumberV2(totalScrap)} {mat.unit}
                            </div>
                            {mat.confirmedDate ? (
                              <small className="flex items-center gap-1 italic text-green-500">
                                <AiOutlineCheckCircle className="text-green-500 " />
                                {mat.confirmedBy} (
                                {formatDateISO(mat.confirmedDate)})
                              </small>
                            ) : null}
                          </Card>
                        ) : (
                          <></>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                {wasteRejectPkgMvs.length > 0 ? (
                  <div className="space-y-2 ">
                    <div className="flex items-end gap-1">
                      <TbPackageOff size={26} className="text-red-500" />
                      <p className="text-lg font-bold underline ">
                        รายการ Reject PKG (คืนเสีย)
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {wasteRejectPkgMvs.map((mat) => {
                        const totalReject = ceil2Decimal(
                          mat.rejectLots.reduce(
                            (prev, cur) => prev + Number(cur.returnWasteQty),
                            0
                          )
                        );
                        const firstLot = mat.rejectLots[0];
                        return (
                          <Card key={mat.materialCode} variation="elevated">
                            <h6 className="font-bold ">
                              {firstLot.materialDescription}
                            </h6>
                            <p className="mb-3 ">{mat.materialCode}</p>
                            <div className="space-y-2 ">
                              {mat.rejectLots.map((lot) => (
                                <div
                                  key={lot.batchLotNo}
                                  className="flex justify-between "
                                >
                                  <div>
                                    <p>• LOT : {lot.batchLotNo}</p>
                                    <p className="flex items-center gap-1 text-xs italic text-muted">
                                      <AiOutlineEdit className="text-muted " />
                                      บันทึกโดย {lot.recordedBy} (
                                      {formatDateFromUnix(lot.recordedDateTime)}
                                      )
                                    </p>
                                    <p className="flex items-center gap-1 text-xs italic text-green-500">
                                      <AiOutlineCheckCircle className="text-green-500 " />
                                      อนุมัติโดย QC {lot.approvedWasteReturnBy}{" "}
                                      (
                                      {formatDateFromUnix(
                                        lot.approvedWasteReturnDateTime
                                      )}
                                      )
                                    </p>
                                  </div>
                                  <span>
                                    {formatNumberV2(lot.returnWasteQty)}{" "}
                                    {lot.materialUnit}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <div
                              className={` flex justify-end items-baseline mt-3 font-bold text-xl text-red-500 before:content-['รวม'] before:text-sm before:mr-2 before:text-black`}
                            >
                              {formatNumberV2(totalReject)}{" "}
                              {firstLot.materialUnit}
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                {returns.length > 0 ? (
                  <div className="space-y-2 ">
                    <div className="flex items-end gap-1">
                      <TbPackageImport size={26} className="text-green-500" />
                      <p className="text-lg font-bold underline">
                        รายการคืนของ
                      </p>
                    </div>
                    <DataGrid
                      className="mt-4"
                      style={{ height: "100%", contain: "none" }}
                      columns={returnCols}
                      rows={returns}
                      defaultColumnOptions={{ resizable: true }}
                      rowHeight={(row) => {
                        if (row.type === "GROUP") return 35;
                        const items = row.row.extraReturnItems
                          ? row.row.extraReturnItems.filter(
                              (itm) =>
                                (itm?.qty || itm?.withShQty || itm?.wasteQty!) >
                                0
                            )
                          : [];
                        return (items.length === 0 ? 1 : items.length) * 100;
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </TabItem>
            <TabItem
              title={
                <div className="flex items-center justify-center gap-1">
                  <CiReceipt />
                  Receipt
                </div>
              }
            >
              {order && 
              <RetainRecord
                isFilling={movements.filter(mv => mv.materialType === 'BULK').length > 0}
                title='Order Receipt'
                disabledInput={true}
                orderFromParent={order}
                receiptsFromParent={orderReceipts}
              />}
            </TabItem>
            <TabItem
              title={
                <div className="flex items-center justify-center gap-1">
                  <MdTimeline />
                  Activity Timeline
                </div>
              }
            >
              <div className="grid grid-cols-1 gap-3 p-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6">
                <Lable
                  title="ออกเบิกโดย"
                  value={picklist.releasedBy!}
                  value2={picklist.releasedDate!}
                  badge='Production'
                  badgeVariant="success"
                />
                <Lable
                  title="อนุมัติเบิกโดย"
                  value={picklist.requestedBy!}
                  value2={formatDateFromUnix(picklist.requestedDateTime)}
                  badge='Production'
                  badgeVariant="success"
                />
                <Lable
                  title="อนุมัติจ่ายโดย"
                  value={picklist.approvedBy!}
                  value2={formatDateFromUnix(picklist.approvedDateTime)}
                  badge='Warehouse'
                  badgeVariant="warning"
                />
                <Lable
                  title="ตรวจรับของโดย"
                  value={picklist.checkedBy!}
                  value2={formatDateFromUnix(picklist.checkedDateTime)}
                  badge='Production'
                  badgeVariant="success"
                />
                {picklist.qualityCheckedBy ? (
                  <Lable
                    title="ตรวจก่อน Mix โดย"
                    value={picklist.qualityCheckedBy}
                    value2={formatDateFromUnix(picklist.qualityCheckedDateTime)}
                    badge='Production'
                    badgeVariant="success"
                  />
                ) : null}
                <Lable
                  title="เริ่มผลิตโดย"
                  value={firstLog ? firstLog.createdBy! : "-"}
                  value2={`${
                    firstLog ? formatDateFromUnix(firstLog.startedTime) : "-"
                  }`}
                  badge='Production'
                  badgeVariant="success"
                />
                <Lable
                  title="ผลิตจบโดย"
                  value={latestDraftedOp.draftedBy!}
                  value2={`${formatDateISO(latestDraftedOp.draftedDate, true)}`}
                  badge='Production'
                  badgeVariant="success"
                />
                <Lable
                  title="ยืนยันการผลิตโดย"
                  value={lastOp.confirmedBy!}
                  value2={formatDateISO(lastOp.confirmedDate, true)}
                  badge='Production'
                  badgeVariant="success"
                />
                <Lable
                  title="อนุมัติของคืน/ปิดเอกสารโดย"
                  value={picklist.completedBy!}
                  value2={formatDateFromUnix(picklist.completedDateTime)}
                  badge='Production'
                  badgeVariant="success"
                />
                {returns.length > 0 ? (
                  <Lable
                    title="อนุมัติรับคืนโดย"
                    value={returns[returns.length - 1].approvedBy!}
                    value2={formatDateFromUnix(
                      returns[returns.length - 1].approvedDateTime
                    )}
                    badge='Warehouse'
                    badgeVariant="warning"
                  />
                ) : null}
              </div>
            </TabItem>
            <TabItem
              title={
                <div className="flex items-center justify-center gap-1">
                  <BiCommentDetail />
                  Comments <Badge variant="info">{`${comments.length}`}</Badge>
                </div>
              }
            >
              <div className="p-5 ">
                <Comment
                  comments={comments}
                  onPost={(c) => {}}
                  readonly={true}
                  hideTitle={true}
                />
              </div>
            </TabItem>
          </Tabs>
        </>
      ) : (
        isSubmitting && <SpinnerLoading />
      )}
    </div>
  );
});

const displayOrderStatus = (status: string): ReactElement => {
  let variant = "";
  if (status === "PD_READY") variant = "outline-primary";
  else if (status === "PD_IN_PROGRESS") variant = "primary";
  else if (status === "PD_DRAFTED") variant = "outline-success";
  else if (status === "PD_CONFIRMED") variant = "success";
  else if (status === "PD_CANCELED") variant = "secondary";

  let label = "";
  if (status === "PD_READY") label = "พร้อมผลิต";
  else if (status === "PD_IN_PROGRESS") label = "กำลังผลิต";
  else if (status === "PD_DRAFTED") label = "ผลิตจบแล้ว";
  else if (status === "PD_CONFIRMED") label = "ยืนยันแล้ว";
  else if (status === "PD_CANCELED") label = "ยกเลิก";

  return <Badge variant={variant}>{label}</Badge>;
};

const displayPicklistStatus = (status: string): ReactElement => {
  let variant = "";
  if (status === "PD_RELEASED") variant = "outline-primary";
  else if (status === "PD_REQUESTED") variant = "primary";
  else if (status === "WH_SHORTAGE") variant = "outline-success";
  else if (status === "WH_APPROVED") variant = "success";
  else if (status === "PD_CHECKED") variant = "new";
  else if (status === "QA_CHECKED") variant = "warning";
  else if (status === "PD_RECORDED") variant = "danger";
  else if (status === "PICKLIST_COMPLETED") variant = "info";

  let label = "";
  if (status === "PD_RELEASED") label = "ออกเบิกแล้ว";
  else if (status === "PD_REQUESTED") label = "อนุมัติเบิกแล้ว";
  else if (status === "WH_SHORTAGE") label = "อนุมัติจ่ายแล้ว";
  else if (status === "WH_APPROVED") label = "อนุมัติจ่ายบางส่วน";
  else if (status === "PD_CHECKED") label = "ตรวจสอบแล้ว";
  else if (status === "QA_CHECKED") label = "ตรวจคุณภาพแล้ว";
  else if (status === "PD_RECORDED") label = "บันทึกยอดใช้แล้ว";
  else if (status === "PICKLIST_COMPLETED") label = "ปิดเอกสารแล้ว";

  return <Badge variant={variant}>{label}</Badge>;
};

const extractItems = (
  headers: DgtPdOrderPicklistExtraReturn[]
): DgtPdOrderPicklistExtraReturnItem[] => {
  let totalItems: DgtPdOrderPicklistExtraReturnItem[] = [];
  for (const header of headers) {
    const items = header.items ? JSON.parse(header.items) : [];
    totalItems = [...totalItems, ...items];
  }
  return totalItems;
};
